import React, { useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  IconButton,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  MoreVert as MoreIcon,
  QrCode as QrCodeIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PackageBooking } from '../../../services/bookingService';
import BookingDetailDialog from './BookingDetailDialog';

interface VenueBookingsTableProps {
  bookings: PackageBooking[];
  loading: boolean;
  onRedeemBooking: (code: string) => Promise<{success: boolean; booking?: PackageBooking; error?: string}>;
  venueId: string;
}

const VenueBookingsTable: React.FC<VenueBookingsTableProps> = ({
  bookings,
  loading,
  onRedeemBooking,
  venueId
}) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedBooking, setSelectedBooking] = useState<PackageBooking | null>(null);
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false);
  const [redemptionCode, setRedemptionCode] = useState('');
  const [redemptionLoading, setRedemptionLoading] = useState(false);
  const [redemptionError, setRedemptionError] = useState<string | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle view booking details
  const handleViewBookingDetails = (booking: PackageBooking) => {
    setSelectedBooking(booking);
    setDetailDialogOpen(true);
  };

  // Handle redeem button click
  const handleRedeemButtonClick = (booking: PackageBooking) => {
    setSelectedBooking(booking);
    setRedemptionCode(booking.redemptionCode);
    setRedeemDialogOpen(true);
  };

  // Handle redeem dialog close
  const handleRedeemDialogClose = () => {
    setRedeemDialogOpen(false);
    setRedemptionCode('');
    setRedemptionError(null);
  };

  // Handle redemption code submission
  const handleRedeemSubmit = async () => {
    if (!redemptionCode.trim()) {
      setRedemptionError('Please enter a redemption code');
      return;
    }

    setRedemptionLoading(true);
    setRedemptionError(null);

    try {
      const result = await onRedeemBooking(redemptionCode);
      
      if (result.success) {
        handleRedeemDialogClose();
      } else {
        setRedemptionError(result.error || 'Failed to redeem code');
      }
    } catch (error) {
      console.error('Error redeeming code:', error);
      setRedemptionError('An unexpected error occurred');
    } finally {
      setRedemptionLoading(false);
    }
  };

  // Handle booking status display
  const getStatusChip = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <Chip label="Confirmed" color="primary" size="small" />;
      case 'completed':
        return <Chip label="Completed" color="success" size="small" />;
      case 'cancelled':
        return <Chip label="Cancelled" color="error" size="small" />;
      case 'refunded':
        return <Chip label="Refunded" color="warning" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (bookings.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          No bookings found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          There are no bookings matching your current filters.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Paper>
        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Venue</TableCell>
                <TableCell>Package</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Guests</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Code</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bookings
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((booking) => {
                  const bookingDate = booking.bookingDate?.toDate();
                  const isRedeemable = booking.status === 'confirmed' && 
                                     bookingDate && 
                                     new Date(bookingDate) <= new Date();
                  
                  // Calculate price per person (if available)
                  const pricePerPerson = booking.amount && booking.guests 
                    ? (booking.amount / booking.guests)
                    : booking.amount || 0;
                  
                  // Format currency
                  const formatCurrency = (amount: number) => {
                    return new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 2
                    }).format(amount);
                  };
                  
                  return (
                    <TableRow key={booking.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {booking.userName || 'Anonymous'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.userEmail}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {booking.venueName || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>{booking.packageName}</TableCell>
                      <TableCell>
                        <Tooltip title="Price per person" arrow placement="top">
                          <Typography variant="body2">
                            {booking.amount ? formatCurrency(pricePerPerson) : '$0.00'}
                            <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.5 }}>
                              /person
                            </Typography>
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{booking.guests}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {booking.amount ? formatCurrency(booking.amount) : '$0.00'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {bookingDate ? bookingDate.toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {`${booking.timeSlot.start} - ${booking.timeSlot.end}`}
                      </TableCell>
                      <TableCell>{getStatusChip(booking.status)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {booking.redemptionCode}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            onClick={() => handleViewBookingDetails(booking)}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isRedeemable && (
                          <Tooltip title="Redeem Booking">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleRedeemButtonClick(booking)}
                            >
                              <QrCodeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={bookings.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Redemption Dialog */}
      <Dialog open={redeemDialogOpen} onClose={handleRedeemDialogClose}>
        <DialogTitle>Redeem Booking</DialogTitle>
        <DialogContent>
          <Box sx={{ width: '100%', maxWidth: 400, pt: 1 }}>
            <Typography variant="body2" paragraph>
              Enter or scan the redemption code to verify and mark this booking as redeemed.
            </Typography>
            <TextField
              label="Redemption Code"
              value={redemptionCode}
              onChange={(e) => setRedemptionCode(e.target.value)}
              fullWidth
              margin="normal"
              variant="outlined"
              error={!!redemptionError}
              helperText={redemptionError}
              disabled={redemptionLoading}
              InputProps={{
                endAdornment: <QrCodeIcon color="action" />
              }}
            />
            {selectedBooking && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Booking Details:</Typography>
                <Typography variant="body2">
                  Package: {selectedBooking.packageName}
                </Typography>
                <Typography variant="body2">
                  Customer: {selectedBooking.userName}
                </Typography>
                <Typography variant="body2">
                  Date: {selectedBooking.bookingDate?.toDate().toLocaleDateString()}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRedeemDialogClose} disabled={redemptionLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleRedeemSubmit} 
            variant="contained" 
            color="primary"
            disabled={redemptionLoading}
            startIcon={redemptionLoading ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {redemptionLoading ? 'Processing...' : 'Redeem'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Booking Detail Dialog */}
      {selectedBooking && (
        <BookingDetailDialog
          open={detailDialogOpen}
          onClose={() => setDetailDialogOpen(false)}
          booking={selectedBooking}
          onRedeemBooking={onRedeemBooking}
        />
      )}
    </>
  );
};

export default VenueBookingsTable;
